import { supabase } from '@shared/config/supabase';
import { IAcademicRepository } from '../../domain/repositories/IAcademicRepository';
import { Level } from '../../domain/entities/Level';
import { Subject } from '../../domain/entities/Subject';
import { Assignment } from '../../domain/entities/Assignment';
import {
  SubjectWithCourseDTO,
  SubjectFormData,
  ScheduleDTO,
} from '../../application/dto/SubjectDTO';
import type { Database } from '@/shared/types/database.types';

type CoursesRow = Database['public']['Tables']['Courses']['Row'];
type SubjectsRow = Database['public']['Tables']['Subjects']['Row'];

/** Shape returned by the Courses query with Subjects + users joins in getAssignments */
interface CourseWithSubjectAndTeacher {
  id: CoursesRow['id'];
  subject_id: number;
  Subjects: { name: string; Levels: { workspace_id: number } } | null;
  users: { full_name: string | null } | null;
}

/** Shape returned by the Enrollments query with users join */
interface EnrollmentWithUser {
  student_id: string | null;
  users: { id: string; full_name: string | null; email: string | null } | null;
}

/** Shape returned by the Subjects query with Levels join */
interface SubjectWithLevels extends SubjectsRow {
  Levels: { name: string | null } | null;
}

/** Shape returned by Courses query with Subjects join (inner) for getAvailableStudentsForCourse */
interface CourseWithSubjects {
  id: CoursesRow['id'];
  academic_cycle_id: CoursesRow['academic_cycle_id'];
  Subjects: { level_id: number } | null;
}

/**
 * Supabase Academic Repository
 * Implements IAcademicRepository using Supabase as the backend
 *
 * Database Tables:
 * - Levels: Academic levels (id, workspace_id, name, sort_order)
 * - Subjects: Academic subjects (id, level_id, name, description)
 * - Courses: Links subjects to teachers
 * - Enrollments: Student enrollments in courses
 */
/**
 * Generate a URL-friendly slug from a string
 */
function generateSlug(name: string): string {
  const baseSlug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

  // Add random suffix to ensure uniqueness
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  return `${baseSlug}-${randomSuffix}`;
}

export class SupabaseAcademicRepository implements IAcademicRepository {
  private workspaceId: number;

  constructor(workspaceId: number) {
    this.workspaceId = workspaceId;
  }

  // ============ Level Operations ============

  /**
   * Get all academic levels for the workspace with aggregated counts
   */
  async getLevels(workspaceId?: string): Promise<Level[]> {
    const wsId = workspaceId ? parseInt(workspaceId, 10) : this.workspaceId;

    // Fetch levels
    const { data: levels, error: levelsError } = await supabase
      .from('Levels')
      .select('*')
      .eq('workspace_id', wsId)
      .order('sort_order', { ascending: true });

    if (levelsError) {
      console.error('Error fetching levels:', levelsError);
      return [];
    }

    if (!levels || levels.length === 0) {
      return [];
    }

    const levelIds = levels.map((l) => l.id);

    // Fetch subject counts per level (only active subjects)
    const { data: subjectCounts } = await supabase
      .from('Subjects')
      .select('level_id')
      .in('level_id', levelIds)
      .eq('is_active', true);

    const subjectCountMap = new Map<number, number>();
    subjectCounts?.forEach((s) => {
      subjectCountMap.set(s.level_id, (subjectCountMap.get(s.level_id) || 0) + 1);
    });

    // Fetch student counts per level (through Courses -> Enrollments)
    const { data: courses } = await supabase
      .from('Courses')
      .select('id, subject_id, Subjects!inner(level_id)')
      .in('Subjects.level_id', levelIds);

    const coursesByLevel = new Map<number, number[]>();
    courses?.forEach((course) => {
      // Supabase TS infers joins as arrays; at runtime PostgREST returns a single object for many-to-one
      const subject = course.Subjects as unknown as { level_id: number } | null;
      const levelId = subject?.level_id;
      if (levelId) {
        const existing = coursesByLevel.get(levelId) || [];
        existing.push(course.id);
        coursesByLevel.set(levelId, existing);
      }
    });

    // Fetch enrollment counts for all courses
    const allCourseIds = courses?.map((c) => c.id) || [];
    const studentCountMap = new Map<number, number>();

    if (allCourseIds.length > 0) {
      const { data: enrollments } = await supabase
        .from('Enrollments')
        .select('course_id, student_id')
        .in('course_id', allCourseIds);

      // Count unique students per level
      const studentsPerLevel = new Map<number, Set<string>>();
      enrollments?.forEach((e) => {
        // Find which level this course belongs to
        for (const [levelId, courseIds] of coursesByLevel) {
          if (courseIds.includes(e.course_id)) {
            const students = studentsPerLevel.get(levelId) || new Set();
            students.add(e.student_id);
            studentsPerLevel.set(levelId, students);
            break;
          }
        }
      });

      studentsPerLevel.forEach((students, levelId) => {
        studentCountMap.set(levelId, students.size);
      });
    }

    // Map to domain entities
    return levels.map(
      (level) =>
        new Level({
          id: level.id,
          workspaceId: level.workspace_id,
          name: level.name,
          sortOrder: level.sort_order || 0,
          subjectCount: subjectCountMap.get(level.id) || 0,
          studentCount: studentCountMap.get(level.id) || 0,
        })
    );
  }

  /**
   * Find level by ID
   */
  async findLevelById(id: string): Promise<Level | null> {
    const numericId = parseInt(id, 10);

    const { data: level, error } = await supabase
      .from('Levels')
      .select('*')
      .eq('id', numericId)
      .single();

    if (error || !level) {
      return null;
    }

    // Get active subject count
    const { count: subjectCount } = await supabase
      .from('Subjects')
      .select('*', { count: 'exact', head: true })
      .eq('level_id', numericId)
      .eq('is_active', true);

    return new Level({
      id: level.id,
      workspaceId: level.workspace_id,
      name: level.name,
      sortOrder: level.sort_order || 0,
      subjectCount: subjectCount || 0,
    });
  }

  /**
   * Get levels by workspace ID
   */
  async findLevelsByWorkspaceId(workspaceId: string): Promise<Level[]> {
    return this.getLevels(workspaceId);
  }

  /**
   * Create a new level
   */
  async createLevel(level: Level): Promise<Level> {
    // Get the next sort_order for this workspace
    const { data: maxOrder } = await supabase
      .from('Levels')
      .select('sort_order')
      .eq('workspace_id', level.workspaceId)
      .order('sort_order', { ascending: false })
      .limit(1)
      .single();

    const nextSortOrder = (maxOrder?.sort_order || 0) + 1;

    const { data, error } = await supabase
      .from('Levels')
      .insert({
        workspace_id: level.workspaceId,
        name: level.name,
        sort_order: nextSortOrder,
      })
      .select()
      .single();

    if (error || !data) {
      throw new Error(`Failed to create level: ${error?.message}`);
    }

    return new Level({
      id: data.id,
      workspaceId: data.workspace_id,
      name: data.name,
      sortOrder: data.sort_order || 0,
      subjectCount: 0,
      studentCount: 0,
    });
  }

  /**
   * Update an existing level
   */
  async updateLevel(level: Level): Promise<Level> {
    const { data, error } = await supabase
      .from('Levels')
      .update({
        name: level.name,
        sort_order: level.sortOrder,
      })
      .eq('id', level.id)
      .select()
      .single();

    if (error || !data) {
      throw new Error(`Failed to update level: ${error?.message}`);
    }

    return new Level({
      id: data.id,
      workspaceId: data.workspace_id,
      name: data.name,
      sortOrder: data.sort_order || 0,
      subjectCount: level.subjectCount,
      studentCount: level.studentCount,
    });
  }

  /**
   * Delete a level
   */
  async deleteLevel(id: string): Promise<void> {
    const numericId = parseInt(id, 10);

    // Check if level has subjects
    const { count: subjectCount } = await supabase
      .from('Subjects')
      .select('*', { count: 'exact', head: true })
      .eq('level_id', numericId);

    if (subjectCount && subjectCount > 0) {
      throw new Error('No se puede eliminar un nivel que tiene materias asociadas');
    }

    // Perform delete and select to verify it worked
    const { data, error } = await supabase
      .from('Levels')
      .delete()
      .eq('id', numericId)
      .select();

    if (error) {
      throw new Error(`Error al eliminar nivel: ${error.message}`);
    }

    // Check if anything was actually deleted (RLS might block silently)
    if (!data || data.length === 0) {
      // Verify if the level still exists
      const { data: stillExists } = await supabase
        .from('Levels')
        .select('id')
        .eq('id', numericId)
        .single();

      if (stillExists) {
        throw new Error('No tienes permisos para eliminar este nivel');
      }
    }
  }

  // ============ Subject Operations ============

  /**
   * Get all subjects for the workspace with teacher info
   */
  async getSubjects(): Promise<Subject[]> {
    // First get level IDs for this workspace (RLS handles access)
    const { data: levelIds } = await supabase
      .from('Levels')
      .select('id')
      .eq('workspace_id', this.workspaceId);

    if (!levelIds || levelIds.length === 0) {
      return [];
    }

    const levelIdList = levelIds.map((l) => l.id);

    // Fetch active subjects for those levels
    const { data: subjects, error: subjectsError } = await supabase
      .from('Subjects')
      .select(
        `
        *,
        Levels (
          id,
          workspace_id,
          name,
          sort_order
        )
      `
      )
      .in('level_id', levelIdList)
      .eq('is_active', true)
      .order('level_id', { ascending: true });

    if (subjectsError) {
      console.error('Error fetching subjects:', subjectsError);
      return [];
    }

    if (!subjects || subjects.length === 0) {
      return [];
    }

    const subjectIds = subjects.map((s) => s.id);

    // Fetch primary teacher and modality for each subject through Courses (most recent course per subject)
    const { data: courses } = await supabase
      .from('Courses')
      .select(
        `
        id,
        subject_id,
        teacher_id,
        modality,
        is_draft,
        users!Courses_teacher_id_fkey (
          full_name
        )
      `
      )
      .in('subject_id', subjectIds)
      .order('id', { ascending: false });

    // Take the most recent course for each subject (highest id)
    const teacherMap = new Map<number, string>();
    const modalityMap = new Map<number, 'ONSITE' | 'ONLINE'>();
    const courseIdMap = new Map<number, number>();
    const isDraftMap = new Map<number, boolean>();
    courses?.forEach((course) => {
      // Only set if not already set (first one is the most recent due to ordering)
      if (!teacherMap.has(course.subject_id)) {
        courseIdMap.set(course.subject_id, course.id);
        isDraftMap.set(course.subject_id, (course.is_draft as boolean | null) ?? false);
        // Supabase TS infers join as array; at runtime PostgREST returns a single object
        const users = course.users as unknown as { full_name?: string | null } | null;
        if (users?.full_name) {
          teacherMap.set(course.subject_id, users.full_name);
        }
        if (course.modality) {
          modalityMap.set(course.subject_id, course.modality as 'ONSITE' | 'ONLINE');
        }
      }
    });

    // Map to domain entities
    return subjects.map(
      (subject: { id: number; level_id: number; name: string; description?: string | null; is_active?: boolean | null; Levels?: { name?: string | null } | null }) =>
        new Subject({
          id: subject.id,
          levelId: subject.level_id,
          name: subject.name,
          description: subject.description ?? null,
          isActive: subject.is_active ?? true,
          level: subject.Levels?.name || '',
          teacherName: teacherMap.get(subject.id),
          modality: modalityMap.get(subject.id),
          courseId: courseIdMap.get(subject.id),
          isDraft: isDraftMap.get(subject.id),
        })
    );
  }

  /**
   * Find subject by ID
   */
  async findSubjectById(id: string): Promise<Subject | null> {
    const numericId = parseInt(id, 10);

    const { data: subject, error } = await supabase
      .from('Subjects')
      .select(
        `
        *,
        Levels (
          name,
          sort_order
        )
      `
      )
      .eq('id', numericId)
      .single();

    if (error || !subject) {
      return null;
    }

    // Get teacher name from Courses
    const { data: course } = await supabase
      .from('Courses')
      .select(
        `
        users!Courses_teacher_id_fkey (
          full_name
        )
      `
      )
      .eq('subject_id', numericId)
      .limit(1)
      .single();

    return new Subject({
      id: subject.id,
      levelId: subject.level_id,
      name: subject.name,
      description: subject.description,
      isActive: subject.is_active ?? true,
      level: (subject as { Levels?: { name?: string | null } | null }).Levels?.name || '',
      teacherName: (course as { users?: { full_name?: string | null } | null } | null)?.users?.full_name ?? undefined,
    });
  }

  /**
   * Get subjects by level ID
   */
  async findSubjectsByLevelId(levelId: string): Promise<Subject[]> {
    const numericLevelId = parseInt(levelId, 10);

    const { data: subjects, error } = await supabase
      .from('Subjects')
      .select('*')
      .eq('level_id', numericLevelId)
      .eq('is_active', true);

    if (error || !subjects) {
      return [];
    }

    return subjects.map(
      (subject) =>
        new Subject({
          id: subject.id,
          levelId: subject.level_id,
          name: subject.name,
          description: subject.description,
          isActive: subject.is_active ?? true,
        })
    );
  }

  /**
   * Create a new subject
   */
  async createSubject(subject: Subject): Promise<Subject> {
    const { data, error } = await supabase
      .from('Subjects')
      .insert({
        level_id: subject.levelId,
        name: subject.name,
        description: subject.description,
      })
      .select(`
        *,
        Levels (
          name,
          sort_order
        )
      `)
      .single();

    if (error || !data) {
      throw new Error(`Failed to create subject: ${error?.message}`);
    }

    return new Subject({
      id: data.id,
      levelId: data.level_id,
      name: data.name,
      description: data.description,
      isActive: data.is_active ?? true,
      level: (data as { Levels?: { name?: string | null } | null }).Levels?.name || '',
    });
  }

  /**
   * Update an existing subject
   */
  async updateSubject(subject: Subject): Promise<Subject> {
    const { data, error } = await supabase
      .from('Subjects')
      .update({
        name: subject.name,
        description: subject.description,
        level_id: subject.levelId,
      })
      .eq('id', subject.id)
      .select(`
        *,
        Levels (
          name,
          sort_order
        )
      `)
      .single();

    if (error || !data) {
      throw new Error(`Failed to update subject: ${error?.message}`);
    }

    return new Subject({
      id: data.id,
      levelId: data.level_id,
      name: data.name,
      description: data.description,
      isActive: data.is_active ?? true,
      level: (data as { Levels?: { name?: string | null } | null }).Levels?.name || '',
    });
  }

  /**
   * Delete a subject
   *
   * Logic:
   * - If subject has no courses: hard delete
   * - If subject has courses with enrollments: soft delete (is_active = false)
   * - If subject has courses without enrollments: cascade delete (courses + subject)
   */
  async deleteSubject(id: string): Promise<void> {
    const numericId = parseInt(id, 10);

    // 1. Get courses associated with this subject
    const { data: courses } = await supabase
      .from('Courses')
      .select('id')
      .eq('subject_id', numericId);

    // No courses - perform hard delete
    if (!courses || courses.length === 0) {
      const { error } = await supabase
        .from('Subjects')
        .delete()
        .eq('id', numericId);

      if (error) {
        throw new Error(`Error al eliminar materia: ${error.message}`);
      }
      return;
    }

    // 2. Check if any course has enrollments
    const courseIds = courses.map((c) => c.id);
    const { count: enrollmentCount } = await supabase
      .from('Enrollments')
      .select('*', { count: 'exact', head: true })
      .in('course_id', courseIds);

    if (enrollmentCount && enrollmentCount > 0) {
      // Courses have students - soft delete the subject
      const { error } = await supabase
        .from('Subjects')
        .update({ is_active: false })
        .eq('id', numericId);

      if (error) {
        throw new Error(`Error al desactivar materia: ${error.message}`);
      }
      return;
    }

    // 3. Courses exist but no enrollments - cascade delete
    // Delete related data for each course
    for (const courseId of courseIds) {
      await supabase.from('ClassSchedules').delete().eq('course_id', courseId);
      await supabase.from('Lessons').delete().eq('course_id', courseId);
      await supabase.from('Evaluations').delete().eq('course_id', courseId);
    }

    // Delete courses
    const { error: coursesError } = await supabase
      .from('Courses')
      .delete()
      .in('id', courseIds);

    if (coursesError) {
      throw new Error(`Error al eliminar cursos: ${coursesError.message}`);
    }

    // Delete subject
    const { error: subjectError } = await supabase
      .from('Subjects')
      .delete()
      .eq('id', numericId);

    if (subjectError) {
      throw new Error(`Error al eliminar materia: ${subjectError.message}`);
    }
  }

  // ============ Assignment Operations (Legacy) ============

  /**
   * Get all assignments
   * @deprecated Use Course entity for teacher-subject assignments
   */
  async getAssignments(): Promise<Assignment[]> {
    // Get courses with subject and teacher info
    const { data: courses, error } = await supabase
      .from('Courses')
      .select(
        `
        id,
        subject_id,
        Subjects!inner (
          name,
          Levels!inner (
            workspace_id
          )
        ),
        users!Courses_teacher_id_fkey (
          full_name
        )
      `
      )
      .eq('Subjects.Levels.workspace_id', this.workspaceId);

    if (error || !courses) {
      return [];
    }

    return (courses as unknown as CourseWithSubjectAndTeacher[]).map(
      (course) =>
        new Assignment({
          id: course.id,
          subjectId: course.subject_id,
          subjectName: course.Subjects?.name || '',
          professor: course.users?.full_name || 'Sin asignar',
        })
    );
  }

  /**
   * Assign a professor to a subject (creates a Course)
   * @deprecated Use Course entity for teacher-subject assignments
   */
  async assignProfessor(subjectId: number, professor: string): Promise<Assignment> {
    // Find teacher by name
    const { data: teacher } = await supabase
      .from('users')
      .select('id')
      .eq('full_name', professor)
      .in('role', ['TEACHER', 'DIRECTOR'])
      .single();

    if (!teacher) {
      throw new Error('Teacher not found');
    }

    // Get subject info
    const { data: subject } = await supabase
      .from('Subjects')
      .select('name')
      .eq('id', subjectId)
      .single();

    // Create course with generated slug
    const courseSlug = generateSlug(subject?.name || 'course');
    const { data: course, error } = await supabase
      .from('Courses')
      .insert({
        subject_id: subjectId,
        teacher_id: teacher.id,
        slug: courseSlug,
      })
      .select()
      .single();

    if (error || !course) {
      throw new Error(`Failed to assign professor: ${error?.message}`);
    }

    return new Assignment({
      id: course.id,
      subjectId: subjectId,
      subjectName: subject?.name || '',
      professor: professor,
    });
  }

  /**
   * Remove an assignment (deletes a Course)
   * @deprecated Use Course entity for teacher-subject assignments
   */
  async removeAssignment(id: number): Promise<void> {
    // Check if course has enrollments
    const { count } = await supabase
      .from('Enrollments')
      .select('*', { count: 'exact', head: true })
      .eq('course_id', id);

    if (count && count > 0) {
      throw new Error('Cannot remove assignment with enrolled students');
    }

    const { error } = await supabase.from('Courses').delete().eq('id', id);

    if (error) {
      throw new Error(`Failed to remove assignment: ${error.message}`);
    }
  }

  // ============ Teacher Operations ============

  /**
   * Get all teachers for the workspace
   */
  async getTeachers(): Promise<{ id: string; name: string }[]> {
    const { data: teachers, error } = await supabase
      .from('users')
      .select('id, full_name')
      .in('role', ['TEACHER', 'DIRECTOR'])
      .eq('workspace_id', this.workspaceId)
      .order('full_name', { ascending: true });

    if (error) {
      console.error('Error fetching teachers:', error);
      return [];
    }

    return (teachers || []).map((t) => ({
      id: t.id,
      name: t.full_name || 'Sin nombre',
    }));
  }

  /**
   * Assign a teacher to a subject by creating a Course
   */
  async assignTeacherToSubject(subjectId: number, teacherId: string): Promise<void> {
    // Check if a course already exists for this subject
    const { data: existingCourse } = await supabase
      .from('Courses')
      .select('id')
      .eq('subject_id', subjectId)
      .single();

    if (existingCourse) {
      // Update existing course with new teacher
      const { error: updateError } = await supabase
        .from('Courses')
        .update({ teacher_id: teacherId })
        .eq('id', existingCourse.id);

      if (updateError) {
        throw new Error(`Error al actualizar asignacion: ${updateError.message}`);
      }
    } else {
      // Get the subject name for the slug
      const { data: subject } = await supabase
        .from('Subjects')
        .select('name')
        .eq('id', subjectId)
        .single();

      const courseSlug = generateSlug(subject?.name || 'curso');

      // Get current academic cycle
      const now = new Date().toISOString();
      const { data: currentCycle } = await supabase
        .from('AcademicCycles')
        .select('id')
        .eq('workspace_id', this.workspaceId)
        .eq('status', true)
        .lte('start_date', now)
        .gte('end_date', now)
        .limit(1)
        .single();

      if (!currentCycle) {
        // If no current cycle, try to get any active cycle
        const { data: activeCycle } = await supabase
          .from('AcademicCycles')
          .select('id')
          .eq('workspace_id', this.workspaceId)
          .eq('status', true)
          .order('start_date', { ascending: false })
          .limit(1)
          .single();

        if (!activeCycle) {
          throw new Error('No hay un ciclo academico activo. Crea uno primero.');
        }

        // Create new course with active cycle
        const { error: insertError } = await supabase.from('Courses').insert({
          subject_id: subjectId,
          teacher_id: teacherId,
          academic_cycle_id: activeCycle.id,
          modality: 'ONSITE',
          slug: courseSlug,
        });

        if (insertError) {
          throw new Error(`Error al crear asignacion: ${insertError.message}`);
        }
      } else {
        // Create new course with current cycle
        const { error: insertError } = await supabase.from('Courses').insert({
          subject_id: subjectId,
          teacher_id: teacherId,
          academic_cycle_id: currentCycle.id,
          modality: 'ONSITE',
          slug: courseSlug,
        });

        if (insertError) {
          throw new Error(`Error al crear asignacion: ${insertError.message}`);
        }
      }
    }
  }

  /**
   * Get students enrolled in courses belonging to a specific level
   */
  async getStudentsByLevel(
    levelId: number
  ): Promise<{ id: string; name: string; email: string }[]> {
    // 1. Get subjects for this level
    const { data: subjects, error: subjectsError } = await supabase
      .from('Subjects')
      .select('id')
      .eq('level_id', levelId)
      .eq('is_active', true);

    if (subjectsError) {
      console.error('Error fetching subjects:', subjectsError);
      return [];
    }

    if (!subjects || subjects.length === 0) {
      return [];
    }

    const subjectIds = subjects.map((s) => s.id);

    // 2. Get courses for these subjects
    const { data: courses, error: coursesError } = await supabase
      .from('Courses')
      .select('id')
      .in('subject_id', subjectIds);

    if (coursesError) {
      console.error('Error fetching courses:', coursesError);
      return [];
    }

    if (!courses || courses.length === 0) {
      return [];
    }

    const courseIds = courses.map((c) => c.id);

    // 3. Get enrollments for these courses with student info
    const { data: enrollments, error: enrollmentsError } = await supabase
      .from('Enrollments')
      .select(
        `
        student_id,
        users!Enrollments_student_id_fkey (
          id,
          full_name,
          email
        )
      `
      )
      .in('course_id', courseIds);

    if (enrollmentsError) {
      console.error('Error fetching enrollments:', enrollmentsError);
      return [];
    }

    if (!enrollments || enrollments.length === 0) {
      return [];
    }

    // 4. Get unique students
    const studentsMap = new Map<string, { id: string; name: string; email: string }>();

    (enrollments as unknown as EnrollmentWithUser[]).forEach((enrollment) => {
      const user = enrollment.users;
      if (user && !studentsMap.has(user.id)) {
        studentsMap.set(user.id, {
          id: user.id,
          name: user.full_name || 'Sin nombre',
          email: user.email || '',
        });
      }
    });

    return Array.from(studentsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get all students available for enrollment (students not enrolled in any course of the level)
   */
  async getAvailableStudentsForLevel(
    levelId: number
  ): Promise<{ id: string; name: string; email: string }[]> {
    // 1. Get all students with role 'STUDENT' in this workspace
    const { data: allStudents, error: studentsError } = await supabase
      .from('users')
      .select('id, full_name, email')
      .eq('workspace_id', this.workspaceId)
      .eq('role', 'STUDENT');

    if (studentsError) {
      console.error('Error fetching students:', studentsError);
      return [];
    }

    if (!allStudents || allStudents.length === 0) {
      return [];
    }

    // 2. Get students already enrolled in this level
    const enrolledStudents = await this.getStudentsByLevel(levelId);
    const enrolledIds = new Set(enrolledStudents.map((s) => s.id));

    // 3. Filter out already enrolled students
    return allStudents
      .filter((s) => !enrolledIds.has(s.id))
      .map((s) => ({
        id: s.id,
        name: s.full_name || 'Sin nombre',
        email: s.email || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Enroll a student in all courses of a specific level
   * Note: A student can only be in one level at a time.
   * Enrolling in a new level automatically unenrolls from previous levels.
   */
  async enrollStudentInLevel(studentId: string, levelId: number): Promise<void> {
    // 1. Get ALL levels for this workspace to unenroll from other levels first
    const { data: allLevels } = await supabase
      .from('Levels')
      .select('id')
      .eq('workspace_id', this.workspaceId);

    if (allLevels && allLevels.length > 0) {
      // Unenroll from all other levels (not the target level)
      const otherLevelIds = allLevels.map((l) => l.id).filter((id) => id !== levelId);

      for (const otherLevelId of otherLevelIds) {
        await this.unenrollStudentFromLevel(studentId, otherLevelId);
      }
    }

    // 2. Get subjects for this level
    const { data: subjects, error: subjectsError } = await supabase
      .from('Subjects')
      .select('id')
      .eq('level_id', levelId)
      .eq('is_active', true);

    if (subjectsError) {
      throw new Error(`Error al obtener materias: ${subjectsError.message}`);
    }

    if (!subjects || subjects.length === 0) {
      throw new Error('No hay materias activas en este nivel');
    }

    const subjectIds = subjects.map((s) => s.id);

    // 3. Get courses for these subjects
    const { data: courses, error: coursesError } = await supabase
      .from('Courses')
      .select('id')
      .in('subject_id', subjectIds);

    if (coursesError) {
      throw new Error(`Error al obtener cursos: ${coursesError.message}`);
    }

    if (!courses || courses.length === 0) {
      throw new Error('No hay cursos disponibles en este nivel. Asegúrate de que las materias tengan profesores asignados.');
    }

    // 4. Create enrollments for each course
    const enrollments = courses.map((c) => ({
      student_id: studentId,
      course_id: c.id,
      progress_status: 'ENROLLED',
    }));

    const { error: enrollError } = await supabase.from('Enrollments').insert(enrollments);

    if (enrollError) {
      // Check if it's a duplicate error
      if (enrollError.code === '23505') {
        throw new Error('El estudiante ya está inscrito en algunos cursos de este nivel');
      }
      throw new Error(`Error al inscribir estudiante: ${enrollError.message}`);
    }
  }

  /**
   * Remove a student from all courses of a specific level
   */
  async unenrollStudentFromLevel(studentId: string, levelId: number): Promise<void> {
    // 1. Get subjects for this level
    const { data: subjects, error: subjectsError } = await supabase
      .from('Subjects')
      .select('id')
      .eq('level_id', levelId);

    if (subjectsError) {
      throw new Error(`Error al obtener materias: ${subjectsError.message}`);
    }

    if (!subjects || subjects.length === 0) {
      console.log('No subjects found for level', levelId);
      return; // No subjects, nothing to unenroll
    }

    const subjectIds = subjects.map((s) => s.id);
    console.log('Subjects for level', levelId, ':', subjectIds);

    // 2. Get courses for these subjects
    const { data: courses, error: coursesError } = await supabase
      .from('Courses')
      .select('id')
      .in('subject_id', subjectIds);

    if (coursesError) {
      throw new Error(`Error al obtener cursos: ${coursesError.message}`);
    }

    if (!courses || courses.length === 0) {
      console.log('No courses found for subjects', subjectIds);
      return; // No courses, nothing to unenroll
    }

    const courseIds = courses.map((c) => c.id);
    console.log('Courses to unenroll from:', courseIds);

    // 3. First check what enrollments exist
    const { data: existingEnrollments } = await supabase
      .from('Enrollments')
      .select('id, course_id')
      .eq('student_id', studentId)
      .in('course_id', courseIds);

    console.log('Existing enrollments to delete:', existingEnrollments);

    if (!existingEnrollments || existingEnrollments.length === 0) {
      console.log('No enrollments found for student', studentId, 'in courses', courseIds);
      return;
    }

    // 4. Delete enrollments by their IDs directly
    const enrollmentIds = existingEnrollments.map((e) => e.id);

    const { data: deletedData, error: deleteError } = await supabase
      .from('Enrollments')
      .delete()
      .in('id', enrollmentIds)
      .select();

    console.log('Delete result:', { deletedData, deleteError });

    if (deleteError) {
      throw new Error(`Error al desinscribir estudiante: ${deleteError.message}`);
    }

    // Verify deletion actually happened
    if (!deletedData || deletedData.length === 0) {
      // Check if enrollments still exist
      const { data: stillExists } = await supabase
        .from('Enrollments')
        .select('id')
        .in('id', enrollmentIds);

      if (stillExists && stillExists.length > 0) {
        throw new Error('No se pudo eliminar las inscripciones. Verifica los permisos en Supabase.');
      }
    }
  }

  /**
   * Get all students that are not enrolled in any level
   */
  async getStudentsWithoutAnyLevel(): Promise<{ id: string; name: string; email: string }[]> {
    // 1. Get all students with role 'STUDENT' in this workspace
    const { data: allStudents, error: studentsError } = await supabase
      .from('users')
      .select('id, full_name, email')
      .eq('workspace_id', this.workspaceId)
      .eq('role', 'STUDENT');

    if (studentsError) {
      console.error('Error fetching students:', studentsError);
      return [];
    }

    if (!allStudents || allStudents.length === 0) {
      return [];
    }

    // 2. Get all enrollments for students in this workspace
    const studentIds = allStudents.map((s) => s.id);

    const { data: enrollments } = await supabase
      .from('Enrollments')
      .select('student_id')
      .in('student_id', studentIds);

    // 3. Get unique student IDs that have at least one enrollment
    const enrolledStudentIds = new Set(enrollments?.map((e) => e.student_id) || []);

    // 4. Filter to only students without any enrollment
    return allStudents
      .filter((s) => !enrolledStudentIds.has(s.id))
      .map((s) => ({
        id: s.id,
        name: s.full_name || 'Sin nombre',
        email: s.email || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // ============ Subject with Course Operations ============

  /**
   * Get subject with course and schedule details for editing
   */
  async getSubjectWithCourseDetails(subjectId: number): Promise<SubjectWithCourseDTO | null> {
    // 1. Get subject
    const { data: subject, error: subjectError } = await supabase
      .from('Subjects')
      .select(`
        *,
        Levels (name, sort_order)
      `)
      .eq('id', subjectId)
      .single();

    if (subjectError || !subject) {
      console.error('Error fetching subject:', subjectError);
      return null;
    }

    // 2. Get course for this subject (if exists) - get the most recent one
    const { data: courses } = await supabase
      .from('Courses')
      .select('id, teacher_id, modality, is_draft')
      .eq('subject_id', subjectId)
      .order('id', { ascending: false })
      .limit(1);

    const course = courses?.[0] || null;

    // 3. Get schedules for the course (if course exists)
    let schedules: ScheduleDTO[] = [];
    if (course) {
      const { data: scheduleData } = await supabase
        .from('ClassSchedules')
        .select('id, day_of_week, start_hour, start_minute, duration_minutes, room, virtual_link')
        .eq('course_id', course.id)
        .eq('is_active', true)
        .order('day_of_week')
        .order('start_hour');

      schedules = (scheduleData || []).map((s) => ({
        id: s.id,
        dayOfWeek: s.day_of_week,
        startHour: s.start_hour,
        startMinute: s.start_minute,
        durationMinutes: s.duration_minutes,
        room: s.room,
        virtualLink: s.virtual_link,
      }));
    }

    return {
      id: subject.id,
      levelId: subject.level_id,
      name: subject.name,
      description: subject.description,
      isActive: subject.is_active ?? true,
      level: (subject as unknown as SubjectWithLevels).Levels?.name || '',
      courseId: course?.id,
      teacherId: course?.teacher_id,
      modality: course?.modality,
      isDraft: course?.is_draft ?? false,
      schedules,
    };
  }

  /**
   * Helper to get active academic cycle
   */
  private async getActiveAcademicCycle(): Promise<{ id: number } | null> {
    const now = new Date().toISOString();

    // Try to get current cycle (date within range)
    const { data: currentCycle } = await supabase
      .from('AcademicCycles')
      .select('id')
      .eq('workspace_id', this.workspaceId)
      .eq('status', true)
      .lte('start_date', now)
      .gte('end_date', now)
      .limit(1)
      .maybeSingle();

    if (currentCycle) return currentCycle;

    // Fallback to any active cycle
    const { data: activeCycle } = await supabase
      .from('AcademicCycles')
      .select('id')
      .eq('workspace_id', this.workspaceId)
      .eq('status', true)
      .order('start_date', { ascending: false })
      .limit(1)
      .maybeSingle();

    return activeCycle;
  }

  /**
   * Create a subject with optional course and schedules
   */
  async createSubjectWithCourse(data: SubjectFormData): Promise<Subject> {
    // 1. Create Subject
    const { data: subject, error: subjectError } = await supabase
      .from('Subjects')
      .insert({
        level_id: data.levelId,
        name: data.name,
        description: data.description,
      })
      .select(`
        *,
        Levels (name, sort_order)
      `)
      .single();

    if (subjectError || !subject) {
      throw new Error(`Error al crear materia: ${subjectError?.message}`);
    }

    let teacherName: string | undefined;

    // 2. If teacher is selected, create Course
    if (data.teacherId) {
      const academicCycle = await this.getActiveAcademicCycle();

      if (!academicCycle) {
        throw new Error('No hay un ciclo academico activo. Crea uno primero.');
      }

      // Generate a unique slug for the course
      const courseSlug = generateSlug(data.name);

      const { data: course, error: courseError } = await supabase
        .from('Courses')
        .insert({
          subject_id: subject.id,
          teacher_id: data.teacherId,
          academic_cycle_id: academicCycle.id,
          modality: data.modality || 'ONSITE',
          is_draft: data.isDraft ?? false,
          slug: courseSlug,
        })
        .select()
        .single();

      if (courseError || !course) {
        // Rollback subject creation
        await supabase.from('Subjects').delete().eq('id', subject.id);
        throw new Error(`Error al crear curso: ${courseError?.message}`);
      }

      // Get teacher name
      const { data: teacher } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', data.teacherId)
        .single();

      teacherName = teacher?.full_name;

      // 3. If schedules provided, create them
      if (data.schedules && data.schedules.length > 0) {
        const scheduleInserts = data.schedules.map((s) => ({
          course_id: course.id,
          day_of_week: s.dayOfWeek,
          start_hour: s.startHour,
          start_minute: s.startMinute,
          duration_minutes: s.durationMinutes,
          room: s.room || null,
          modality: data.modality || 'ONSITE',
          virtual_link: s.virtualLink || null,
          is_active: true,
        }));

        const { error: scheduleError } = await supabase
          .from('ClassSchedules')
          .insert(scheduleInserts);

        if (scheduleError) {
          console.error('Error creating schedules:', scheduleError);
        }
      }
    }

    return new Subject({
      id: subject.id,
      levelId: subject.level_id,
      name: subject.name,
      description: subject.description,
      isActive: subject.is_active ?? true,
      level: (subject as unknown as SubjectWithLevels).Levels?.name || '',
      teacherName,
    });
  }

  /**
   * Update a subject with optional course and schedules
   */
  async updateSubjectWithCourse(subjectId: number, data: SubjectFormData): Promise<Subject> {
    // 1. Update Subject
    const { data: subject, error: subjectError } = await supabase
      .from('Subjects')
      .update({
        level_id: data.levelId,
        name: data.name,
        description: data.description,
      })
      .eq('id', subjectId)
      .select(`
        *,
        Levels (name, sort_order)
      `)
      .single();

    if (subjectError || !subject) {
      throw new Error(`Error al actualizar materia: ${subjectError?.message}`);
    }

    let teacherName: string | undefined;

    // 2. Get existing course for this subject (get most recent if multiple exist)
    const { data: existingCourses } = await supabase
      .from('Courses')
      .select('id')
      .eq('subject_id', subjectId)
      .order('id', { ascending: false });

    const existingCourse = existingCourses?.[0] || null;

    // If there are multiple courses for this subject, clean up duplicates
    if (existingCourses && existingCourses.length > 1) {
      console.log('[updateSubjectWithCourse] Cleaning up duplicate courses:', existingCourses.length - 1);
      const duplicateIds = existingCourses.slice(1).map(c => c.id);
      // Delete schedules for duplicate courses
      await supabase.from('ClassSchedules').delete().in('course_id', duplicateIds);
      // Delete duplicate courses
      await supabase.from('Courses').delete().in('id', duplicateIds);
    }

    if (data.teacherId) {
      // Teacher is selected
      if (existingCourse) {
        // Update existing course
        const { error: courseError } = await supabase
          .from('Courses')
          .update({
            teacher_id: data.teacherId,
            modality: data.modality || 'ONSITE',
            is_draft: data.isDraft ?? false,
          })
          .eq('id', existingCourse.id);

        if (courseError) {
          throw new Error(`Error al actualizar curso: ${courseError.message}`);
        }

        // Delete existing schedules and recreate
        await supabase
          .from('ClassSchedules')
          .delete()
          .eq('course_id', existingCourse.id);

        // Create new schedules
        if (data.schedules && data.schedules.length > 0) {
          const scheduleInserts = data.schedules.map((s) => ({
            course_id: existingCourse.id,
            day_of_week: s.dayOfWeek,
            start_hour: s.startHour,
            start_minute: s.startMinute,
            duration_minutes: s.durationMinutes,
            room: s.room || null,
            modality: data.modality || 'ONSITE',
            virtual_link: s.virtualLink || null,
            is_active: true,
          }));

          await supabase.from('ClassSchedules').insert(scheduleInserts);
        }
      } else {
        // Create new course
        const academicCycle = await this.getActiveAcademicCycle();

        if (!academicCycle) {
          throw new Error('No hay un ciclo academico activo. Crea uno primero.');
        }

        // Generate slug from subject name
        const courseSlug = generateSlug(data.name);

        const { data: course, error: courseError } = await supabase
          .from('Courses')
          .insert({
            subject_id: subjectId,
            teacher_id: data.teacherId,
            academic_cycle_id: academicCycle.id,
            modality: data.modality || 'ONSITE',
            is_draft: data.isDraft ?? false,
            slug: courseSlug,
          })
          .select()
          .single();

        if (courseError || !course) {
          throw new Error(`Error al crear curso: ${courseError?.message}`);
        }

        // Create schedules
        if (data.schedules && data.schedules.length > 0) {
          const scheduleInserts = data.schedules.map((s) => ({
            course_id: course.id,
            day_of_week: s.dayOfWeek,
            start_hour: s.startHour,
            start_minute: s.startMinute,
            duration_minutes: s.durationMinutes,
            room: s.room || null,
            modality: data.modality || 'ONSITE',
            virtual_link: s.virtualLink || null,
            is_active: true,
          }));

          await supabase.from('ClassSchedules').insert(scheduleInserts);
        }
      }

      // Get teacher name
      const { data: teacher } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', data.teacherId)
        .single();

      teacherName = teacher?.full_name;
    } else if (existingCourse) {
      // No teacher selected but course exists - delete course and schedules
      await supabase.from('ClassSchedules').delete().eq('course_id', existingCourse.id);
      await supabase.from('Courses').delete().eq('id', existingCourse.id);
    }

    return new Subject({
      id: subject.id,
      levelId: subject.level_id,
      name: subject.name,
      description: subject.description,
      isActive: subject.is_active ?? true,
      level: (subject as unknown as SubjectWithLevels).Levels?.name || '',
      teacherName,
    });
  }

  // ============ Course-Level Enrollment Operations ============

  /**
   * Get students enrolled in a specific course
   */
  async getStudentsByCourse(courseId: number): Promise<{ id: string; name: string; email: string }[]> {
    const { data: enrollments, error } = await supabase
      .from('Enrollments')
      .select(`
        student_id,
        users!Enrollments_student_id_fkey (
          id,
          full_name,
          email
        )
      `)
      .eq('course_id', courseId);

    if (error) {
      console.error('Error fetching course students:', error);
      return [];
    }

    return ((enrollments || []) as unknown as EnrollmentWithUser[])
      .filter((e) => e.users)
      .map((e) => ({
        id: e.users!.id,
        name: e.users!.full_name || 'Sin nombre',
        email: e.users!.email || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get students from the level who are NOT enrolled in this course
   */
  async getAvailableStudentsForCourse(courseId: number): Promise<{ id: string; name: string; email: string }[]> {
    // 1. Get the level for this course
    const { data: course, error: courseError } = await supabase
      .from('Courses')
      .select(`
        id,
        academic_cycle_id,
        Subjects!inner (
          level_id
        )
      `)
      .eq('id', courseId)
      .single();

    if (courseError || !course) {
      console.error('Error fetching course:', courseError);
      return [];
    }

    const levelId = (course as unknown as CourseWithSubjects).Subjects?.level_id;
    if (!levelId) return [];

    // 2. Get all students enrolled in ANY course of this level (same cycle)
    const { data: levelSubjects } = await supabase
      .from('Subjects')
      .select('id')
      .eq('level_id', levelId);

    if (!levelSubjects || levelSubjects.length === 0) return [];

    const subjectIds = levelSubjects.map(s => s.id);

    const { data: levelCourses } = await supabase
      .from('Courses')
      .select('id')
      .eq('academic_cycle_id', course.academic_cycle_id)
      .in('subject_id', subjectIds);

    if (!levelCourses || levelCourses.length === 0) return [];

    const levelCourseIds = levelCourses.map(c => c.id);

    // Get students enrolled in any course of this level
    const { data: levelEnrollments } = await supabase
      .from('Enrollments')
      .select(`
        student_id,
        users!Enrollments_student_id_fkey (
          id,
          full_name,
          email
        )
      `)
      .in('course_id', levelCourseIds);

    // Get unique students from the level
    const levelStudentsMap = new Map<string, { id: string; name: string; email: string }>();
    ((levelEnrollments || []) as unknown as EnrollmentWithUser[]).forEach((e) => {
      if (e.users && !levelStudentsMap.has(e.users.id)) {
        levelStudentsMap.set(e.users.id, {
          id: e.users.id,
          name: e.users.full_name || 'Sin nombre',
          email: e.users.email || '',
        });
      }
    });

    // 3. Get students already enrolled in THIS course
    const enrolledInCourse = await this.getStudentsByCourse(courseId);
    const enrolledIds = new Set(enrolledInCourse.map(s => s.id));

    // 4. Return students from level who are NOT in this course
    return Array.from(levelStudentsMap.values())
      .filter(s => !enrolledIds.has(s.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Enroll a single student in a specific course
   */
  async enrollStudentInCourse(studentId: string, courseId: number): Promise<void> {
    const { error } = await supabase
      .from('Enrollments')
      .insert({
        student_id: studentId,
        course_id: courseId,
        progress_status: 'ENROLLED',
      });

    if (error) {
      if (error.code === '23505') {
        throw new Error('El estudiante ya está inscrito en este curso');
      }
      throw new Error(`Error al inscribir estudiante: ${error.message}`);
    }
  }

  /**
   * Unenroll a student from a specific course
   */
  async unenrollStudentFromCourse(studentId: string, courseId: number): Promise<void> {
    const { error } = await supabase
      .from('Enrollments')
      .delete()
      .eq('student_id', studentId)
      .eq('course_id', courseId);

    if (error) {
      throw new Error(`Error al desinscribir estudiante: ${error.message}`);
    }
  }

  /**
   * Enroll all students from the level into a specific course (repair function)
   * Uses the database function created in migration-auto-enroll-trigger.sql
   */
  async enrollAllLevelStudentsInCourse(courseId: number): Promise<number> {
    const { data, error } = await supabase
      .rpc('enroll_level_students_in_course', { p_course_id: courseId });

    if (error) {
      throw new Error(`Error al inscribir estudiantes: ${error.message}`);
    }

    return data || 0;
  }
}
